import * as React from 'react'
import { connect } from 'react-redux'

import { filteredRatesSelector, offerStyleIsCompactSelector } from 'embed/selectors'
import { DepositsOfferProductSubType, isFixedSizeEmbed, OfferOverflowList } from 'embed/constants'
import { MultiProductState } from 'embed/reducers'

import { fetchRateTables, seeAllDepositsSelected, setMaxResults } from 'common/actions'
import QueryParams from 'common/services/QueryParams'
import { DepositsOffer } from 'embed/constants/models'
import { DepositsStep, Filter, Product, ProductResponseKeys, QueryParamKeys } from 'even-ts-static'
import { OfferListView } from './index'
import { uiSelectedDepositsStepSelector } from 'embed/selectors/ui'

export const ADDITIONAL_OFFERS_COUNT = 25

interface Props {
  currentMaxResults: number
  depositsOffers: DepositsOffer[]
  dispatchFetchDepositsRateTables: () => void
  dispatchSeeAllDepositsSelected: () => void
  dispatchSetMaxResults: (maxResults: number) => void
  offerOverflow: OfferOverflowList
  offerStyleIsCompact: boolean
  seeMoreOffersAvailable: boolean
  seeAllOffersAvailable: boolean
  step: DepositsStep
  productSubTypeFilter: unknown
}

const OfferListContainer: React.FC<Props> = ({
  currentMaxResults,
  dispatchFetchDepositsRateTables,
  dispatchSetMaxResults,
  step,
  ...other
}) => {
  const [depositAmountModalClosed, toggleDepositAmountModalClosed] = React.useState(false)

  React.useEffect(() => {
    if (step === DepositsStep.SeeMore) {
      dispatchSetMaxResults(ADDITIONAL_OFFERS_COUNT)
    }
  }, [dispatchSetMaxResults, step])

  const dispatchSeeMoreDepositsSelected = React.useCallback(() => {
    dispatchSetMaxResults(currentMaxResults + ADDITIONAL_OFFERS_COUNT)
  }, [currentMaxResults, dispatchSetMaxResults])

  const onDepositAmountSelected = React.useCallback(() => {
    dispatchFetchDepositsRateTables()
    toggleDepositAmountModalClosed(true)
  }, [dispatchFetchDepositsRateTables])

  return (
    <OfferListView
      dispatchSeeMoreDepositsSelected={dispatchSeeMoreDepositsSelected}
      onDepositAmountSelected={onDepositAmountSelected}
      shouldShowDepositAmountModal={!depositAmountModalClosed}
      {...other}
    />
  )
}

const mapStateToProps = (state: MultiProductState) => {
  const depositsOffers = filteredRatesSelector(state, {
    offersPath: `${Product.Deposits}.${ProductResponseKeys[Product.Deposits]}Offers`,
    product: Product.Deposits,
  })

  const step = uiSelectedDepositsStepSelector(state)
  const totalAvailableOffers = state.rates[Product.Deposits]?.[`${ProductResponseKeys[Product.Deposits]}Offers`].length
  const pageSize = state.config[QueryParamKeys.PageSize]

  const productSubTypeFilter: DepositsOfferProductSubType =
    state.filters[Product.Deposits]?.[Filter.ProductSubType] ?? ''
  const hasPartnerKeyQueryParam = QueryParams.get(QueryParamKeys.Partner) !== undefined
  const offerOverflow = state.config[QueryParamKeys.OfferOverflow]

  const seeAllOffersAvailable =
    pageSize !== undefined &&
    totalAvailableOffers > pageSize &&
    hasPartnerKeyQueryParam &&
    step === DepositsStep.OfferList &&
    (!isFixedSizeEmbed() || offerOverflow === OfferOverflowList.PARTNER_LINK)

  const seeMoreOffersAvailable =
    pageSize !== undefined && totalAvailableOffers > pageSize && step === DepositsStep.SeeMore

  const visibleOffers = seeAllOffersAvailable ? depositsOffers.slice(0, pageSize) : depositsOffers

  return {
    currentMaxResults: state.rates.maxResults[Product.Deposits] ?? 3,
    depositsOffers: visibleOffers,
    offerOverflow,
    offerStyleIsCompact: offerStyleIsCompactSelector(state),
    productSubTypeFilter,
    seeAllOffersAvailable,
    seeMoreOffersAvailable,
    step,
  }
}

const mapDispatchToProps = (dispatch) => ({
  dispatchFetchDepositsRateTables: () => dispatch(fetchRateTables(Product.Deposits)),
  dispatchSeeAllDepositsSelected: () => dispatch(seeAllDepositsSelected()),
  dispatchSetMaxResults: (maxResults) => dispatch(setMaxResults(Product.Deposits, maxResults)),
})

export const OfferList = connect(mapStateToProps, mapDispatchToProps)(OfferListContainer)
